import React from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Theme, Typography, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { CalendarDayObject } from '../../../../screens/App/FamilyMember/FamilyCalendar/Calendar'
import { useSelector } from 'react-redux'
import { getMembers } from '../../../../store/data/data.selectors'
import { CustomDateObject, MemberResponse } from '../../../../shared/types'
import CakeIcon from '@mui/icons-material/Cake'
import DeathIcon from '../../../../shared/assets/icons/DeathIcon'
import { getAppTheme } from '../../../../shared/config/getAppTheme'
import { getPrefersDarkMode } from '../../../../store/settings/settings.selectors'

interface IProps {
  index: number
  day: CalendarDayObject
  selectedDate?: number
  calendarData?: any
  setSelectedDate?: (selectedDate: number) => void
  changeSelectedDate?: (date: number) => void
}

const CalendarDay: React.FC<IProps> = ({ day, index, ...props }) => {
  const { t } = useTranslation()
  const theme: Theme = getAppTheme()
  const darkMode = useSelector(getPrefersDarkMode)
  const downLg = useMediaQuery(theme.breakpoints.down('lg'))
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))

  const position = day && new Date(day.year, day.month - 1, day.day).getTime()
  const members: MemberResponse | undefined = useSelector(getMembers)

  const useStyles = makeStyles((theme: Theme) => ({
    calendarDaysDay: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      width: '15%',
      maxWidth: '15%',
      minHeight: '50px',
      height: 'auto',
      borderRight: `1px solid ${darkMode ? '#8a8a8a' : '#f5f5f5'}`,
      backgroundColor: theme.palette.background.paper,
      transition: 'all .2s ease-in-out',
    },
    dayNumber: {
      padding: '10px 12px',
      textAlign: 'right',
      color: day.actualMonth ? (day.actualDay ? '#646464' : '#B1B1B1') : '#EBEBEB',
      fontWeight: day.actualDay ? 700 : 500,
    },
    postCards: {},
    dropArea: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      position: 'relative',
      padding: '0px !important',
      '&::before': {
        content: `'${t('coreCalendar:drop')}'`,
        color: '#646464',
        fontSize: '.7em',
        textTransform: 'uppercase',
        padding: '14px',
        width: '100%',
        height: '100%',
        border: '2px dashed rgba(100, 100, 100, 0.5)',
        position: 'absolute',
        display: 'flex',
        justifyContent: 'flex-start',
      },
    },
    dropAreaRestricted: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      padding: '0px !important',
      '&::before': {
        content: '\'×\'',
        color: 'red',
        fontSize: '1.3em',
        textTransform: 'uppercase',
        padding: '5px 12px',
        width: '100%',
        height: '100%',
        position: 'absolute',
        display: 'flex',
        justifyContent: 'flex-start',
      },
    },
  }))

  const classes = useStyles()

  const hasBirthday = (date: CustomDateObject) => {
    if (day.month === date.month && day.day === date.day) {
      if (date.year) {
        return {
          birthday: true,
          age: day.year >= date.year ? day.year - date.year : null,
        }
      } else {
        return {
          birthday: true,
        }
      }
    } else {
      return {
        birthday: false,
      }
    }
  }

  return (
    <Box
      key={position}
      data-position={position}
      className={`${classes.calendarDaysDay}`}
      component='div'
    >
      <Typography variant='body2' component='span' className={classes.dayNumber}>{day.day}</Typography>
      {day.actualDay &&
        <Box sx={{
          position: 'absolute',
          width: '30px',
          height: '30px',
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: '15px',
          top: '4px',
          right: day.day < 10 ? '2px' : '4px',
        }}
        />}
      <Box className={classes.postCards} component='div'>
        {members?.results.map(member => {
          if (member.showDetails && hasBirthday(member.birthday).birthday) {
            return (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '10px',
                    width: 'calc(100% - 10px)',
                    backgroundColor: member.dead ? theme.palette.grey['500'] : theme.palette.primary.main,
                    color: '#fff',
                    padding: '5px',
                    margin: '5px',
                  }}
                >
                  {member.dead && !downMd && <DeathIcon width='20px' color='#fff' />}
                  <Typography
                    variant='body1'
                    sx={downMd ? {
                      fontSize: '10px',
                      textAlign: 'center',
                    } : (downSm ? { fontSize: '12px' } : (downLg ? { fontSize: '12px' } : { whiteSpace: 'nowrap' }))}
                  >
                    {`${member.firstname} ${downLg ? member.lastname.charAt(0).toUpperCase() + `. (${hasBirthday(member.birthday).age})` : member.lastname}`}&nbsp;
                  </Typography>
                  {hasBirthday(member.birthday).age === 0
                    ? (
                      <>
                        {!downMd && <CakeIcon />}
                      </>
                    )
                    : (
                      <>
                        {!downLg &&
                          <Typography
                            sx={downMd ? { fontSize: '10px' } : (downSm ? { fontSize: '12px' } : (downLg ? { fontSize: '12px' } : {}))}
                            variant='body1'
                          >({hasBirthday(member.birthday).age})
                          </Typography>}
                      </>)}
                </Box>
              </>
            )
          }
        })}
      </Box>
    </Box>
  )
}

export default CalendarDay
